// Admin management for the monthly sponsor banner: one sponsor per calendar
// month (name, logo, optional link + custom thank-you line). The member
// dashboard shows whichever sponsor matches the current month.

import { useEffect, useRef, useState } from 'react';
import { Heart, Pencil, Trash2, Plus, ExternalLink, X } from 'lucide-react';
import { sponsorsAPI } from '../api/client';
import type { Sponsor } from '../types';

interface SponsorForm {
  name: string;
  month: string; // YYYY-MM (input type="month"); ignored when isArchived
  isArchived: boolean;
  link_url: string;
  message: string;
}

const emptyForm = (): SponsorForm => ({
  name: '',
  month: new Date().toISOString().slice(0, 7),
  isArchived: false,
  link_url: '',
  message: '',
});

const monthLabel = (isoDate: string | null | undefined) => {
  if (!isoDate) return null;
  return new Date(`${isoDate.slice(0, 7)}-01T00:00:00`).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
};

const isCurrentMonth = (isoDate: string | null | undefined) =>
  !!isoDate && isoDate.slice(0, 7) === new Date().toISOString().slice(0, 7);

// Admins may type a bare domain ("example.com", "www.example.com") — quietly
// add a scheme so the saved link "just works" without them needing to know.
const normalizeLinkUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed || /^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export default function SponsorsAdmin() {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Sponsor | null>(null);
  const [form, setForm] = useState<SponsorForm>(emptyForm());
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchSponsors = async () => {
    try {
      setSponsors(await sponsorsAPI.list());
    } catch (err) {
      console.error('Error fetching sponsors:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSponsors();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setLogoFile(null);
    setLogoPreview(null);
    setError(null);
    setShowForm(true);
  };

  const openEdit = (sponsor: Sponsor) => {
    setEditing(sponsor);
    setForm({
      name: sponsor.name,
      month: sponsor.month ? sponsor.month.slice(0, 7) : new Date().toISOString().slice(0, 7),
      isArchived: !sponsor.month,
      link_url: sponsor.link_url,
      message: sponsor.message,
    });
    setLogoFile(null);
    setLogoPreview(sponsor.logo);
    setError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setError(null);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setLogoFile(file);
    setLogoPreview(file ? URL.createObjectURL(file) : editing?.logo ?? null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || (!form.isArchived && !form.month)) return;
    setIsSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        month: form.isArchived ? null : `${form.month}-01`,
        link_url: normalizeLinkUrl(form.link_url),
        message: form.message.trim(),
      };
      const saved = editing
        ? await sponsorsAPI.update(editing.id, payload)
        : await sponsorsAPI.create(payload);
      if (logoFile) {
        await sponsorsAPI.uploadLogo(saved.id, logoFile, logoFile.name);
      }
      await fetchSponsors();
      closeForm();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      setError(
        detail ? Object.values(detail).flat().join(' ') : 'Failed to save sponsor.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (sponsor: Sponsor) => {
    const when = monthLabel(sponsor.month) ?? 'Archived';
    if (!window.confirm(`Delete sponsor "${sponsor.name}" (${when})?`)) {
      return;
    }
    try {
      await sponsorsAPI.delete(sponsor.id);
      setSponsors((prev) => prev.filter((s) => s.id !== sponsor.id));
    } catch (err) {
      console.error('Error deleting sponsor:', err);
    }
  };

  const renderSponsorRow = (sponsor: Sponsor) => (
    <div key={sponsor.id} className="flex items-center gap-4 p-4">
      {sponsor.logo ? (
        <img
          src={sponsor.logo}
          alt={`${sponsor.name} logo`}
          className="w-12 h-12 rounded-xl object-contain bg-off-white flex-shrink-0"
        />
      ) : (
        <div className="w-12 h-12 bg-off-white rounded-xl flex items-center justify-center flex-shrink-0">
          <Heart className="text-gold" size={24} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-navy truncate">{sponsor.name}</p>
          {isCurrentMonth(sponsor.month) && (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
              Live now
            </span>
          )}
          {!sponsor.month && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
              Archived
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 truncate">
          {sponsor.month
            ? `${monthLabel(sponsor.month)} \u00b7 ${sponsor.display_message}`
            : sponsor.display_message}
        </p>
      </div>
      {sponsor.link_url && (
        <a
          href={sponsor.link_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${sponsor.name} link`}
          className="text-gray-400 hover:text-navy transition"
        >
          <ExternalLink size={18} />
        </a>
      )}
      <button
        onClick={() => openEdit(sponsor)}
        aria-label={`Edit ${sponsor.name}`}
        className="p-2 text-gray-400 hover:text-navy transition"
      >
        <Pencil size={18} />
      </button>
      <button
        onClick={() => handleDelete(sponsor)}
        aria-label={`Delete ${sponsor.name}`}
        className="p-2 text-gray-400 hover:text-red-600 transition"
      >
        <Trash2 size={18} />
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  const scheduledSponsors = sponsors.filter((s) => s.month);
  const archivedSponsors = sponsors.filter((s) => !s.month);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center">
          <Heart className="text-gold" size={28} />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-navy">Monthly Sponsors</h1>
          <p className="text-gray-600">
            Manage the sponsor thank-you banner shown on the member dashboard
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-light transition text-sm font-medium"
        >
          <Plus size={16} />
          Add Sponsor
        </button>
      </div>

      {/* Create / edit form */}
      {showForm && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-navy">
              {editing ? `Edit ${editing.name}` : 'New Sponsor'}
            </h2>
            <button
              onClick={closeForm}
              aria-label="Close form"
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <X size={20} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sponsor name *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jimmy's Ice Cream"
                  required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduling
                </label>
                <div className="flex gap-4 px-1 py-2.5">
                  <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="scheduling"
                      checked={!form.isArchived}
                      onChange={() => setForm({ ...form, isArchived: false })}
                      className="text-navy focus:ring-navy"
                    />
                    Schedule for a month
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="scheduling"
                      checked={form.isArchived}
                      onChange={() => setForm({ ...form, isArchived: true })}
                      className="text-navy focus:ring-navy"
                    />
                    Archive (save for later)
                  </label>
                </div>
                {!form.isArchived && (
                  <input
                    type="month"
                    value={form.month}
                    onChange={(e) => setForm({ ...form, month: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
                  />
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link (optional)
              </label>
              <input
                type="text"
                value={form.link_url}
                onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                placeholder="www.jimmysicecream.com"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom thank-you message (optional)
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={2}
                placeholder={
                  form.name.trim()
                    ? `This month's Iron Forums app is brought to you by ${form.name.trim()}`
                    : "Leave blank for the default \u201cThis month's Iron Forums app is brought to you by \u2026\u201d line"
                }
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="w-14 h-14 rounded-xl object-contain bg-off-white"
                  />
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleLogoChange}
                  className="text-sm text-gray-600 file:mr-3 file:px-4 file:py-2 file:border-0 file:rounded-lg file:bg-off-white file:text-navy file:font-medium file:cursor-pointer hover:file:bg-gray-100"
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isSaving}
                className="px-5 py-2.5 bg-navy text-white rounded-lg font-medium hover:bg-navy-light transition text-sm disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : editing ? 'Save changes' : 'Create sponsor'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="px-5 py-2.5 bg-off-white text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sponsor list */}
      {sponsors.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 shadow-sm text-center text-gray-400">
          No sponsors yet. Add one to show the thank-you banner on the member dashboard.
        </div>
      ) : (
        <>
          {scheduledSponsors.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Scheduled
              </h2>
              <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100">
                {scheduledSponsors.map((sponsor) => renderSponsorRow(sponsor))}
              </div>
            </div>
          )}
          {archivedSponsors.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Archived
              </h2>
              <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100">
                {archivedSponsors.map((sponsor) => renderSponsorRow(sponsor))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
